import { apiClient } from '@/lib/api/client'
import type { CsatScore } from '@/types/csat'

export interface CsatSubmitResponse {
  message: string
}

export interface CsatSurveyStatus {
  valid?: boolean
  expired?: boolean
  already_rated?: boolean
  expires_at?: string
  message: string
}

export interface CsatApiError {
  message: string
  status?: number
}

/** Extract a user-facing message from axios / Laravel validation errors. */
export function getCsatApiError(err: unknown): CsatApiError {
  const response = (err as { response?: { status?: number; data?: Record<string, unknown> } })
    ?.response

  if (!response) {
    return {
      message:
        'Unable to reach the server. Start the backend (php artisan serve) and set VITE_API_URL=http://127.0.0.1:8000/api in philtower_fe/.env.',
    }
  }

  const data = response.data ?? {}
  if (typeof data.message === 'string' && data.message.trim() !== '') {
    return { message: data.message, status: response.status }
  }

  const errors = data.errors as Record<string, string[]> | undefined
  if (errors) {
    const first = Object.values(errors).flat().find(Boolean)
    if (first) {
      return { message: first, status: response.status }
    }
  }

  return {
    message: 'Something went wrong. Please try again.',
    status: response.status,
  }
}

export async function getCsatSurveyStatus(token: string): Promise<CsatSurveyStatus> {
  const { data } = await apiClient.get<CsatSurveyStatus>(`/support/csat/${token}`)
  return data
}

/** Email one-click links: POST /support/csat/submit with integer score (1–4). */
export async function submitCsatScore(token: string, score: CsatScore): Promise<CsatSubmitResponse> {
  const { data } = await apiClient.post<CsatSubmitResponse>('/support/csat/submit', {
    token,
    score,
  })
  return data
}

/** Interactive /csat/:token star picker. */
export async function submitCsatRating(
  token: string,
  rating: CsatScore | string
): Promise<CsatSubmitResponse> {
  const { data } = await apiClient.post<CsatSubmitResponse>(`/support/csat/${token}`, {
    rating,
  })
  return data
}

/** Alias used by /csat/:token page — submits integer score via existing submit endpoint. */
export const submitCsatByToken = submitCsatScore
